// lib/chatStateManager.js - ENHANCED WITH AGENT JOIN TRACKING
import { fetchMessages, checkActiveChat } from './appwriteChat'
import { ref, readonly, onMounted, onUnmounted } from 'vue'

class ChatStateManager {
  constructor() {
    this.storageKey = 'chatly_chat_state'
    this.listeners = new Set()
    this.messageIds = new Set()
    this.state = {
      customer: null,
      conversation: null,
      messages: [],
      currentPhase: 'start',
      customerName: '',
      isActive: false,
      lastActivity: null,
      companyId: null,
      isAIProcessing: false,
      isRestarting: false,
      // ✅ NEW: Agent tracking
      agentHasJoined: false,        // Track if agent join message was already shown
      agentInfo: null,              // Store agent info
      agentJoinedAt: null,          // When agent joined
      processedAgentIds: new Set()  // Track which agent IDs we've already processed
    }
    
    this.loadFromCache()
  }

  // Add AI processing methods
  setAIProcessing(processing) {
    console.log(`🔧 Setting AI processing: ${processing}`);
    this.state.isAIProcessing = processing;
    this.notifyListeners();
  }

  clearAIProcessing() {
    console.log(`🔧 Clearing AI processing flag`);
    this.state.isAIProcessing = false;
    this.notifyListeners();
  }

  // Force clear AI processing (for debugging stuck states)
  forceResetAIProcessing() {
    console.log(`🚨 FORCE RESET: Clearing stuck AI processing flag`);
    this.state.isAIProcessing = false;
    this.saveToCache();
    this.notifyListeners();
  }

  // ✅ NEW: Check if agent has already joined
  hasAgentJoined() {
    return this.state.agentHasJoined === true;
  }

  // ✅ NEW: Mark agent as joined (prevents duplicate messages)
  markAgentAsJoined(agentInfo) {
    console.log('👤 Marking agent as joined:', agentInfo);
    
    this.state.agentHasJoined = true;
    this.state.agentInfo = agentInfo;
    this.state.agentJoinedAt = new Date().toISOString();
    
    // Track this agent ID as processed
    if (agentInfo?.agentId) {
      this.state.processedAgentIds.add(agentInfo.agentId);
    }
    
    this.saveToCache();
    console.log('✅ Agent marked as joined, will not show join message again');
  }

  // ✅ NEW: Check if specific agent ID was already processed
  isAgentIdProcessed(agentId) {
    return this.state.processedAgentIds.has(agentId);
  }

  // ✅ NEW: Reset agent tracking (for new chats)
  resetAgentTracking() {
    console.log('🔄 Resetting agent tracking for new chat');
    this.state.agentHasJoined = false;
    this.state.agentInfo = null;
    this.state.agentJoinedAt = null;
    this.state.processedAgentIds.clear();
  }

  // Initialize chat state on app start
  async initialize(companyId) {
    console.log('🔄 Initializing chat state...')
    this.state.companyId = companyId
    
    // 1. Try to load from localStorage first (fastest)
    const cachedState = this.loadFromCache()
    if (cachedState) {
      console.log('📱 Loaded cached state:', {
        messageCount: cachedState.messages?.length || 0,
        agentHasJoined: cachedState.agentHasJoined,
        agentInfo: cachedState.agentInfo
      })
      this.state = { ...this.state, ...cachedState }
      
      // ✅ Restore Set from array (localStorage converts Set to array)
      if (cachedState.processedAgentIds && Array.isArray(cachedState.processedAgentIds)) {
        this.state.processedAgentIds = new Set(cachedState.processedAgentIds);
      }
      
      // ✅ CRITICAL: Clear any temporary messages that might have leaked into cache
      this.clearTemporaryMessages();
      
      this.notifyListeners()
    }
    
    // 2. Check if there's an active chat in Appwrite
    try {
      const activeChat = await checkActiveChat()
      if (activeChat.isActive) {
        console.log('☁️ Found active chat on server:', activeChat)
        
        // ✅ Check if agent was already in conversation
        const hasAgentInConversation = activeChat.conversation?.participants?.length > 1 ||
                                      activeChat.conversation?.agentName ||
                                      activeChat.conversation?.agentProfile;
        
        if (hasAgentInConversation && !this.state.agentHasJoined) {
          console.log('👤 Agent detected in existing conversation - marking as already joined');
          this.markAgentAsJoined({
            agentName: activeChat.conversation.agentName || 'Support Agent',
            avatar: activeChat.conversation.agentProfile || null,
            agentId: 'existing-agent'
          });
        }
        
        // Update state with server data
        this.state = {
          ...this.state,
          customer: activeChat.customer,
          conversation: activeChat.conversation,
          messages: activeChat.messages,
          currentPhase: this.determinePhase(activeChat),
          customerName: activeChat.customer?.username || '',
          isActive: true,
          lastActivity: new Date().toISOString()
        }
        
        // Save updated state to cache
        this.saveToCache()
        this.notifyListeners()
      }
    } catch (error) {
      console.error('❌ Error checking active chat:', error)
    }
    
    console.log('✅ Chat state initialized:', this.getDebugInfo())
    return this.state
  }

  // Save current state to localStorage
  saveToCache() {
    try {
      // ✅ FIXED: Filter out typing indicators and temporary messages from cache
      const messagesToCache = this.state.messages
        .filter(msg => !msg.isTyping && !msg.isTemp) // Don't cache typing indicators or temp messages
        .slice(-50); // Keep last 50 non-temporary messages
      
      const cacheData = {
        customer: this.state.customer,
        conversation: this.state.conversation,
        messages: messagesToCache, // Only cache permanent messages
        currentPhase: this.state.currentPhase,
        customerName: this.state.customerName,
        isActive: this.state.isActive,
        lastActivity: this.state.lastActivity,
        companyId: this.state.companyId,
        // ✅ NEW: Save agent tracking data
        agentHasJoined: this.state.agentHasJoined,
        agentInfo: this.state.agentInfo,
        agentJoinedAt: this.state.agentJoinedAt,
        processedAgentIds: Array.from(this.state.processedAgentIds), // Convert Set to Array for JSON
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(cacheData))
      console.log('💾 State saved to cache', {
        totalMessages: this.state.messages.length,
        cachedMessages: messagesToCache.length,
        filteredOut: this.state.messages.length - messagesToCache.length
      })
    } catch (error) {
      console.error('❌ Error saving to cache:', error)
    }
  }

  // Load state from localStorage
  loadFromCache() {
    try {
      const cached = localStorage.getItem(this.storageKey)
      if (!cached) {
        console.log('📭 No stored customer info or conversation ID found')
        return null
      }
      
      const data = JSON.parse(cached)
      
      // Check if cache is not too old (24 hours)
      const cacheAge = new Date() - new Date(data.timestamp)
      if (cacheAge > 24 * 60 * 60 * 1000) {
        console.log('🗑️ Cache too old, clearing')
        this.clearCache()
        return null
      }
      
      return data
    } catch (error) {
      console.error('❌ Error loading from cache:', error)
      return null
    }
  }

  // Clear cache
  clearCache() {
    localStorage.removeItem(this.storageKey)
    console.log('🗑️ Cache cleared')
  }

  // Subscribe to state changes
  subscribe(listener) {
    this.listeners.add(listener)
    console.log(`🔗 Added state listener (total: ${this.listeners.size})`)
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener)
      console.log(`🔗 Removed state listener (total: ${this.listeners.size})`)
    }
  }

  // Enhanced notify listeners with better debugging
  notifyListeners() {
    console.log('📢 Broadcasting state update to listeners:', {
      listenerCount: this.listeners.size,
      messageCount: this.state.messages?.length || 0,
      phase: this.state.currentPhase,
      hasCustomer: !!this.state.customer,
      hasConversation: !!this.state.conversation,
      isRestarting: this.state.isRestarting,
      agentHasJoined: this.state.agentHasJoined
    })
    
    this.listeners.forEach(listener => {
      try {
        listener(this.state)
      } catch (error) {
        console.error('❌ Error in state listener:', error)
      }
    })
  }

  // Update chat state
  updateState(updates) {
    const oldPhase = this.state.currentPhase
    this.state = { ...this.state, ...updates }
    this.state.lastActivity = new Date().toISOString()
    
    // Log phase changes
    if (updates.currentPhase && oldPhase !== updates.currentPhase) {
      console.log(`📱 Phase changed: ${oldPhase} → ${updates.currentPhase}`)
    }
    
    this.saveToCache()
    this.notifyListeners()
  }

  // ✅ ENHANCED: Add message with agent join detection
  addMessage(message) {
    const messageWithId = {
      ...message,
      id: message.id || `msg-${Date.now()}-${Math.random()}`,
      timestamp: message.timestamp || new Date()
    }
    
    // Prevent duplicate messages
    if (this.messageIds.has(messageWithId.id)) {
      console.log(`⚠️ Duplicate message prevented:`, messageWithId.id);
      return;
    }

    // Additional duplicate check by content and timestamp
    const isDuplicate = this.state.messages.some(existingMsg => 
      existingMsg.content === messageWithId.content && 
      existingMsg.type === messageWithId.type &&
      Math.abs(new Date(existingMsg.timestamp) - new Date(messageWithId.timestamp)) < 3000
    );

    if (isDuplicate) {
      console.log('🚫 Duplicate message detected, skipping:', messageWithId.content?.substring(0, 50) + '...');
      return;
    }

    // ✅ NEW: Special handling for agent-joined messages
    if (messageWithId.type === 'agent-joined') {
      // Check if we already have an agent join message
      const existingAgentJoinMessage = this.state.messages.find(msg => msg.type === 'agent-joined');
      
      if (existingAgentJoinMessage || this.state.agentHasJoined) {
        console.log('🚫 Agent join message already exists, skipping duplicate');
        return;
      }
      
      // Mark agent as joined when adding the message
      this.markAgentAsJoined(messageWithId.agentInfo);
    }

    // Add unique ID tracking
    this.messageIds.add(messageWithId.id);
    
    this.state.messages.push(messageWithId)
    this.state.lastActivity = new Date().toISOString()
    
    console.log('➕ Message added to state:', {
      type: messageWithId.type,
      content: messageWithId.content?.substring(0, 50) + '...',
      totalMessages: this.state.messages.length
    })
    
    this.saveToCache()
    this.notifyListeners()
  }

  // ✅ ENHANCED: Remove all typing messages
  removeTypingMessage() {
    const initialCount = this.state.messages.length;
    this.state.messages = this.state.messages.filter(msg => !msg.isTyping);
    const removedCount = initialCount - this.state.messages.length;
    
    if (removedCount > 0) {
      console.log(`🗑️ Removed ${removedCount} typing message(s)`);
      this.saveToCache();
      this.notifyListeners();
    }
  }

  // ✅ NEW: Add typing indicator (temporary, won't be cached)
  addTypingIndicator(senderInfo = null) {
    // First remove any existing typing indicators
    this.removeTypingMessage();
    
    const typingMessage = {
      id: `typing-${Date.now()}`,
      type: senderInfo?.type || 'ai',
      content: 'tippt...',
      isTyping: true,
      isTemp: true, // Mark as temporary
      timestamp: new Date(),
      agentInfo: senderInfo?.agentInfo || null
    };
    
    this.state.messages.push(typingMessage);
    console.log('💬 Typing indicator added (temporary)');
    
    // Don't save to cache, just notify listeners
    this.notifyListeners();
    
    return typingMessage;
  }

  // ✅ NEW: Clear all temporary messages (typing indicators, etc.)
  clearTemporaryMessages() {
    const initialCount = this.state.messages.length;
    this.state.messages = this.state.messages.filter(msg => !msg.isTemp && !msg.isTyping);
    const removedCount = initialCount - this.state.messages.length;
    
    if (removedCount > 0) {
      console.log(`🗑️ Removed ${removedCount} temporary message(s)`);
      this.notifyListeners();
    }
  }

  // Clear messages for fresh start
  clearMessages() {
    this.state.messages = [];
    this.messageIds.clear();
    console.log('🧹 All messages cleared')
    this.saveToCache();
    this.notifyListeners();
  }

  // Start a new chat session
  startNewChat(customer, conversation, customerName) {
    console.log('🚀 Starting new chat session:', { customer, conversation, customerName })
    
    // ✅ FIXED: Preserve existing greeting messages when starting new chat
    const existingGreetingMessages = this.state.messages.filter(msg => 
      msg.type === 'ai' && 
      msg.content && 
      !msg.isTyping &&
      (msg.content.includes('Wie kann ich dir heute') || 
       msg.content.includes('helfen') ||
       msg.content.includes('NEX AI') ||
       msg.content.includes('Hallo') ||
       msg.content.includes('Guten Tag') ||
       msg.content.includes('KI-Assistent') ||
       msg.content.includes('behilflich') ||
       msg.content.includes('Persönlicher'))
    );
    
    console.log('💬 Preserving greeting messages during chat start:', {
      greetingCount: existingGreetingMessages.length,
      totalMessages: this.state.messages.length
    });
    
    // Clear message tracking for new chat
    this.messageIds.clear();
    
    // Re-add preserved message IDs
    existingGreetingMessages.forEach(msg => {
      if (msg.id) {
        this.messageIds.add(msg.id);
      }
    });
    
    // ✅ Reset agent tracking for new chat
    this.resetAgentTracking();
    
    this.state = {
      ...this.state,
      customer,
      conversation,
      customerName,
      messages: existingGreetingMessages, // ✅ FIXED: Preserve greeting messages instead of clearing all
      currentPhase: 'ai',
      isActive: true,
      isRestarting: false,
      lastActivity: new Date().toISOString()
    }
    
    this.saveToCache()
    this.notifyListeners()
  }

  // End chat session
  endChat() {
    console.log('🏁 Ending chat session')
    
    this.state = {
      ...this.state,
      currentPhase: 'end',
      isActive: false,
      lastActivity: new Date().toISOString()
    }
    
    this.saveToCache()
    this.notifyListeners()
  }

  // ✅ ENHANCED COMPLETE RESTART with agent tracking reset
  completeRestart() {
    console.log('🔄 COMPLETE RESTART - clearing everything');
    
    // 1. Clear message tracking
    this.messageIds.clear();
    
    // 2. Clear all localStorage keys that might interfere
    const keysToRemove = [
      'chatly_chat_state',
      'chat_customer_id',
      'chat_customer_email', 
      'chat_customer_name',
      'chat_customer_anonymous_id',
      'chat_conversation_id',
      'chatState'
    ];
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`🗑️ Cleared localStorage: ${key}`);
      } catch (error) {
        console.error(`❌ Error clearing ${key}:`, error);
      }
    });
    
    // 3. Store company ID before reset
    const currentCompanyId = this.state.companyId;
    
    // 4. Reset to completely fresh initial state
    this.state = {
      customer: null,
      conversation: null,
      messages: [],
      currentPhase: 'start',
      customerName: '',
      isActive: false,
      lastActivity: null,
      companyId: currentCompanyId, // Keep company ID
      isAIProcessing: false,
      isRestarting: true,
      // ✅ Reset agent tracking
      agentHasJoined: false,
      agentInfo: null,
      agentJoinedAt: null,
      processedAgentIds: new Set()
    };
    
    // 5. Save fresh state and notify all listeners immediately
    this.saveToCache()
    console.log('📢 Broadcasting complete restart to all listeners')
    this.notifyListeners()
    
    // 6. Clear restart flag after components have time to react
    setTimeout(() => {
      this.state.isRestarting = false;
      this.saveToCache();
      this.notifyListeners();
      console.log('✅ Complete restart finished - ready for new chat');
    }, 200); // Give components time to detect restart
  }
  
  // ✅ Helper method to check if this is a fresh restart state
  isRestartState() {
    return this.state.messages.length === 0 && 
           this.state.currentPhase === 'start' && 
           !this.state.customer && 
           !this.state.conversation;
  }

  // ✅ Helper method to check if restart is in progress
  isRestarting() {
    return this.state.isRestarting === true;
  }
  
  // Determine phase based on chat data
  determinePhase(activeChat) {
    if (!activeChat.conversation) return 'start'
    
    // Check if conversation is ended
    if (activeChat.conversation.conversationStatus === 'ended') {
      return 'end'
    }
    
    // Check if human agent is present
    const hasHumanAgent = activeChat.conversation.participants?.length > 1
    if (hasHumanAgent) {
      return 'human'
    }
    
    // Default to AI phase if conversation exists
    return 'ai'
  }

  // Get current state (immutable copy)
  getState() {
    return { ...this.state }
  }

  // Check if messages exist
  hasMessages() {
    return this.state.messages && this.state.messages.length > 0;
  }

  // Check if intro messages exist (AI greeting)
  hasIntroMessages() {
    return this.state.messages.some(msg => 
      msg.content && msg.content.includes('NEX AI') && msg.type === 'ai'
    );
  }

  // Check if any customer messages exist
  hasCustomerMessages() {
    return this.state.messages.some(msg => msg.type === 'customer');
  }

  // Get message count by type
  getMessageCount(type = null) {
    if (!type) return this.state.messages.length;
    return this.state.messages.filter(msg => msg.type === type).length;
  }

// Enhanced syncWithServer method for chatStateManager.js
// Replace the existing syncWithServer method with this enhanced version

// Sync with server (call periodically or on focus)
async syncWithServer() {
  if (!this.state.conversation || !this.state.isActive) {
    console.log('🔄 Skipping sync - no active conversation');
    return;
  }

  try {
    console.log('🔄 Syncing with server for conversation:', this.state.conversation.$id);
    
    // Fetch latest messages from server
    const latestMessages = await fetchMessages(this.state.conversation.$id);
    
    console.log('📨 Server sync results:', {
      serverMessageCount: latestMessages.length,
      currentStateCount: this.state.messages.filter(m => !m.isTemp && !m.isTyping).length,
      currentTotalCount: this.state.messages.length
    });
    
    // ✅ ENHANCED: Always update with server messages to ensure consistency
    const tempMessages = this.state.messages.filter(m => m.isTemp || m.isTyping);
    const nonTempCurrentMessages = this.state.messages.filter(m => !m.isTemp && !m.isTyping);
    
    // Check if server has more messages than our current non-temp messages
    if (latestMessages.length > nonTempCurrentMessages.length) {
      console.log('📨 New messages found on server, updating local state');
      
      // ✅ CRITICAL: Transform server messages to match our local format
      const transformedServerMessages = latestMessages.map(serverMsg => {
        // Check if we already have this message locally
        const existingMessage = this.state.messages.find(localMsg => 
          localMsg.id === serverMsg.$id || 
          (localMsg.content === serverMsg.content && 
           Math.abs(new Date(localMsg.timestamp) - new Date(serverMsg.created_at)) < 5000)
        );
        
        if (existingMessage) {
          // Return existing message to preserve local modifications
          return existingMessage;
        }
        
        // Transform server message to local format
        const transformedMessage = {
          id: serverMsg.$id,
          content: serverMsg.content,
          timestamp: new Date(serverMsg.created_at),
          senderId: serverMsg.sender_id,
          messageType: serverMsg.message_type || 'text'
        };
        
        // ✅ Handle different sender types
        if (serverMsg.sender_type === 'agent') {
          transformedMessage.type = 'agent';
          // Try to get agent info from conversation or use fallback
          if (this.state.conversation?.agentName) {
            transformedMessage.agentInfo = {
              agentName: this.state.conversation.agentName,
              avatar: this.state.conversation.agentProfile || null,
              agentId: serverMsg.sender_id
            };
          }
        } else if (serverMsg.sender_type === 'ai') {
          transformedMessage.type = 'ai';
        } else if (serverMsg.sender_type === 'customer') {
          transformedMessage.type = 'customer';
        } else if (serverMsg.sender_type === 'system') {
          transformedMessage.type = 'system';
        } else {
          // Default fallback
          transformedMessage.type = serverMsg.sender_type || 'system';
        }
        
        // Handle image messages
        if (serverMsg.message_type === 'image') {
          transformedMessage.imageUrl = serverMsg.image_url;
          transformedMessage.fileName = serverMsg.file_name;
          transformedMessage.fileSize = serverMsg.file_size;
          transformedMessage.mimeType = serverMsg.mime_type;
        }
        
        console.log('🔄 Transformed server message:', {
          id: transformedMessage.id,
          type: transformedMessage.type,
          content: transformedMessage.content?.substring(0, 50) + '...'
        });
        
        return transformedMessage;
      });
      
      // ✅ Update messages: server messages + any temporary messages
      this.state.messages = [...transformedServerMessages, ...tempMessages];
      
      // ✅ Update message ID tracking
      this.messageIds.clear();
      transformedServerMessages.forEach(msg => {
        if (msg.id) {
          this.messageIds.add(msg.id);
        }
      });
      
      console.log('✅ Messages updated from server sync:', {
        totalMessages: this.state.messages.length,
        serverMessages: transformedServerMessages.length,
        tempMessages: tempMessages.length
      });
      
      this.saveToCache();
      this.notifyListeners();
      
    } else if (latestMessages.length === nonTempCurrentMessages.length) {
      console.log('📨 Message counts match, checking for content updates');
      
      // Check if any message content has changed
      let hasChanges = false;
      const updatedMessages = [...this.state.messages];
      
      latestMessages.forEach(serverMsg => {
        const localMsgIndex = updatedMessages.findIndex(localMsg => 
          localMsg.id === serverMsg.$id
        );
        
        if (localMsgIndex !== -1) {
          const localMsg = updatedMessages[localMsgIndex];
          // Check if content or other properties have changed
          if (localMsg.content !== serverMsg.content || 
              localMsg.messageType !== serverMsg.message_type) {
            
            console.log('📝 Message content updated:', {
              id: serverMsg.$id,
              oldContent: localMsg.content?.substring(0, 30),
              newContent: serverMsg.content?.substring(0, 30)
            });
            
            // Update the message with server data
            updatedMessages[localMsgIndex] = {
              ...localMsg,
              content: serverMsg.content,
              messageType: serverMsg.message_type || 'text'
            };
            hasChanges = true;
          }
        }
      });
      
      if (hasChanges) {
        this.state.messages = updatedMessages;
        this.saveToCache();
        this.notifyListeners();
        console.log('✅ Message content updated from server');
      }
      
    } else {
      console.log('📨 No new messages from server');
    }
    
  } catch (error) {
    console.error('❌ Error syncing with server:', error);
  }
}

  // Enhanced debug info
  getDebugInfo() {
    return {
      messagesCount: this.state.messages.length,
      messagesByType: {
        ai: this.getMessageCount('ai'),
        customer: this.getMessageCount('customer'),
        agent: this.getMessageCount('agent'),
        system: this.getMessageCount('system'),
        'agent-joined': this.getMessageCount('agent-joined')
      },
      hasCustomer: !!this.state.customer,
      hasConversation: !!this.state.conversation,
      currentPhase: this.state.currentPhase,
      isActive: this.state.isActive,
      isRestarting: this.state.isRestarting,
      customerName: this.state.customerName,
      lastActivity: this.state.lastActivity,
      companyId: this.state.companyId,
      listenerCount: this.listeners.size,
      // ✅ NEW: Agent tracking debug info
      agentHasJoined: this.state.agentHasJoined,
      agentInfo: this.state.agentInfo,
      agentJoinedAt: this.state.agentJoinedAt,
      processedAgentIds: Array.from(this.state.processedAgentIds)
    }
  }

  // Force state refresh (useful for debugging)
  forceRefresh() {
    console.log('🔄 Forcing state refresh')
    this.notifyListeners()
  }
}

// Create singleton instance
export const chatStateManager = new ChatStateManager()

// Vue 3 composable for easy use in components
export function useChatState() {
  const state = ref(chatStateManager.getState())
  
  onMounted(() => {
    const unsubscribe = chatStateManager.subscribe((newState) => {
      state.value = newState
    })
    
    onUnmounted(unsubscribe)
  })
  
  return {
    state: readonly(state),
    addMessage: chatStateManager.addMessage.bind(chatStateManager),
    removeTypingMessage: chatStateManager.removeTypingMessage.bind(chatStateManager),
    updateState: chatStateManager.updateState.bind(chatStateManager),
    startNewChat: chatStateManager.startNewChat.bind(chatStateManager),
    endChat: chatStateManager.endChat.bind(chatStateManager),
    completeRestart: chatStateManager.completeRestart.bind(chatStateManager),
    clearMessages: chatStateManager.clearMessages.bind(chatStateManager),
    hasMessages: chatStateManager.hasMessages.bind(chatStateManager),
    hasIntroMessages: chatStateManager.hasIntroMessages.bind(chatStateManager),
    hasCustomerMessages: chatStateManager.hasCustomerMessages.bind(chatStateManager),
    isRestartState: chatStateManager.isRestartState.bind(chatStateManager),
    isRestarting: chatStateManager.isRestarting.bind(chatStateManager),
    syncWithServer: chatStateManager.syncWithServer.bind(chatStateManager),
    getDebugInfo: chatStateManager.getDebugInfo.bind(chatStateManager),
    forceRefresh: chatStateManager.forceRefresh.bind(chatStateManager),
    // ✅ NEW: Agent tracking methods
    hasAgentJoined: chatStateManager.hasAgentJoined.bind(chatStateManager),
    markAgentAsJoined: chatStateManager.markAgentAsJoined.bind(chatStateManager),
    isAgentIdProcessed: chatStateManager.isAgentIdProcessed.bind(chatStateManager),
    resetAgentTracking: chatStateManager.resetAgentTracking.bind(chatStateManager),
    // ✅ NEW: AI processing methods
    setAIProcessing: chatStateManager.setAIProcessing.bind(chatStateManager),
    clearAIProcessing: chatStateManager.clearAIProcessing.bind(chatStateManager),
    forceResetAIProcessing: chatStateManager.forceResetAIProcessing.bind(chatStateManager)
  }
}

// Debug helper for development
if (typeof window !== 'undefined') {
  window.chatDebug = {
    state: () => chatStateManager.getState(),
    debug: () => chatStateManager.getDebugInfo(),
    restart: () => chatStateManager.completeRestart(),
    clearLocal: () => {
      localStorage.clear();
      console.log('🗑️ All localStorage cleared - reloading page');
      window.location.reload();
    },
    addTestMessage: (content = 'Test message') => {
      chatStateManager.addMessage({
        type: 'customer',
        content: content
      });
    },
    // ✅ NEW: Debug agent tracking
    agentStatus: () => ({
      hasJoined: chatStateManager.hasAgentJoined(),
      agentInfo: chatStateManager.getState().agentInfo,
      processedIds: Array.from(chatStateManager.getState().processedAgentIds)
    }),
    resetAgent: () => {
      chatStateManager.resetAgentTracking();
      console.log('🔄 Agent tracking reset');
    },
    // ✅ NEW: Debug AI processing
    aiStatus: () => ({
      isProcessing: chatStateManager.getState().isAIProcessing,
      state: chatStateManager.getState()
    }),
    forceResetAI: () => {
      chatStateManager.forceResetAIProcessing();
      console.log('🚨 AI processing flag force reset');
    },
    // Force restart and reload page
    hardReset: () => {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  }
  
  console.log('🛠️ Chat debug tools available: window.chatDebug')
}

export default chatStateManager
